import type { NotificationDto } from 'api';

/**
 * Mapa `entityType` (discriminador del backend) → ruta de **listado** del módulo en el frontend.
 *
 * Navegamos al listado (no al detalle por id) porque el patrón de detalle no es uniforme entre
 * módulos —`edit/:id` en facturas/órdenes, `:id` en solicitudes de material, y algunos listados
 * no tienen vista de detalle—. El listado siempre existe y lleva al área correcta sin arriesgar
 * un 404. Cuando se valide el patrón de detalle de un `entityType` concreto se puede afinar aquí.
 *
 * Excepciones que navegan al **detalle** (ver `routeFor`): `SupportTicket` e `Item`. En ambos el
 * backend garantiza que `entityId` es el id que consume la ruta `:id` del detalle.
 *
 * `Item` corresponde a las notificaciones de costo actualizado (`inventory.cost-updated`,
 * `purchasing.cost-updated`, `pricing.price-updated`): `entityId` es el `itemId` del producto y se
 * navega a su ficha. Ver `docs/plans/2026-07-02-recalculo-precios-notificaciones-necesidades-backend.md`.
 *
 * Las claves siguen los `entityType` del contrato del backend (PascalCase).
 */
const ENTITY_ROUTE_MAP: Readonly<Record<string, readonly string[]>> = {
  Invoice: ['/sales/invoices'],
  // Las notas de crédito no tienen listado propio: se gestionan desde el listado de facturas.
  CreditNote: ['/sales/invoices'],
  DeliveryNote: ['/sales/delivery-notes'],
  SaleOrder: ['/sales/orders'],
  DeliveryQuotation: ['/sales/delivery-quotations'],
  InventoryMovement: ['/inventory/movements-v2'],
  BankAccount: ['/finance/accounts'],
  FinancialTransaction: ['/finance/transactions'],
  PaymentRequest: ['/finance/payment-requests'],
  // `credit.limit-exceeded` trae el id del cliente (guía §8); el crédito se consulta por cliente.
  Customer: ['/crm/customers'],
  // `ecommerce.account-requested` (§7): solicitud pública de acceso al portal. entityId = accountId,
  // pero navegamos al listado (patrón del módulo); el prefiltro por estado es mejora futura.
  EcommerceCustomerAccount: ['/ecommerce/customers'],
  MaterialRequest: ['/purchases/material-requests'],
  PurchaseReceipt: ['/purchases/purchase-receipts'],
  Contract: ['/rrhh/contracts'],
  VacationRequest: ['/rrhh/vacations'],
  PosSession: ['/pos/settings/sessions'],
  // PosPrintJob queda sin mapear a propósito: no existe un listado de trabajos de impresión.
};

/**
 * Bases de la ruta de **detalle** de un ticket de soporte. La misma notificación se despacha al
 * reportante (tenant) y a los agentes (master), pero el DTO no indica la vista: la elegimos por
 * el rol del usuario logueado (ver `RouteContext.isSupportAgent`).
 *
 * Las bases coinciden con `app.routes.ts`: el panel del master se monta en `/soporte-admin`
 * (`@admin/support-tickets/support-tickets-admin.routes`) y la vista del tenant en `/support`
 * (`@support/support.routes`). Mantener sincronizado si cambian esos paths.
 */
const SUPPORT_TICKET_DETAIL = {
  /** Agente de soporte (usuario del tenant master) → panel admin/provider en `/soporte-admin`. */
  agent: '/soporte-admin/tickets',
  /** Cualquier otro usuario (tenant cliente) → vista del tenant en `/support`. */
  tenant: '/support/tickets',
} as const;

/** Base de la ruta de **detalle** de un producto (`edit/:id`). `entityId` = `itemId`. */
const ITEM_DETAIL_BASE = '/inventory/products/edit';

/**
 * Contexto del usuario logueado necesario para resolver destinos que dependen del rol.
 * Lo provee el componente que dispara la navegación (no se puede derivar del `NotificationDto`).
 */
export interface RouteContext {
  /**
   * `true` si el usuario es agente de soporte (pertenece al tenant master, `AuthService.isAdmin()`).
   * Decide si una notificación de `SupportTicket` abre la vista admin/provider (`/soporte-admin`,
   * protegida por `isDefaultTenantGuard`) o la del tenant (`/support`). Usamos `isAdmin()` y no el
   * permiso `soporte-tickets-gestionar` porque ese es el predicado que controla el acceso real a la
   * ruta admin; el permiso se corto-circuita para todo admin del master y dejaría rebotar a un
   * usuario no-master que lo tuviera asignado.
   */
  isSupportAgent?: boolean;
}

/**
 * Resuelve los comandos de navegación (`router.navigate(...)` / `routerLink`) para una
 * notificación, a partir de su `entityType`. Devuelve `null` cuando no hay forma segura de
 * navegar: sin `entityType`, sin `entityId`, o `entityType` no mapeado todavía.
 *
 * `SupportTicket` es el único caso que navega al **detalle** (`base/:id` con `entityId`) y que
 * depende del rol del usuario (`ctx.isSupportAgent`); el resto navega al listado del módulo.
 */
export function routeFor(notification: NotificationDto, ctx: RouteContext = {}): string[] | null {
  const entityType = notification.entityType;
  const entityId = notification.entityId;
  if (!entityType || !entityId) return null;

  if (entityType === 'Milestone') {
    try {
      const payload: unknown = JSON.parse(notification.payloadJson ?? '{}');
      const projectId =
        typeof payload === 'object' && payload !== null && 'projectId' in payload ? payload.projectId : null;
      if (typeof projectId === 'string' && projectId) return ['/proyectos', projectId, 'hitos', entityId];
    } catch {
      /* Old notifications may have an invalid payload; keep the projects entry point. */
    }
    return ['/proyectos'];
  }

  if (entityType === 'ProjectTask') return ['/proyectos/tarea', entityId];

  if (entityType === 'SupportTicket') {
    const base = ctx.isSupportAgent ? SUPPORT_TICKET_DETAIL.agent : SUPPORT_TICKET_DETAIL.tenant;
    return [base, entityId];
  }

  // Notificaciones de costo actualizado: `entityId` es el `itemId` → ficha del producto.
  if (entityType === 'Item') {
    return [ITEM_DETAIL_BASE, entityId];
  }

  const base = ENTITY_ROUTE_MAP[entityType];
  if (!base) return null;

  return [...base];
}

/**
 * Indica si la notificación tiene un destino de navegación conocido. No depende del rol: para
 * `SupportTicket` ambas vistas existen, así que basta con que haya `entityType` + `entityId`.
 */
export function hasRoute(notification: NotificationDto): boolean {
  return routeFor(notification) !== null;
}
